import { RootState } from "..";

import { classUserSelectors, createAppSelector, createStringArraySelector } from "./base";
import {
  selectCurrentBreakoutGroup,
  selectCurrentBreakoutGroupId,
  selectIsInBreakout,
} from "./breakouts";
import { selectActiveClassSession, selectIsClassSessionVirtual } from "./class-session";
import { selectCurrentClassUser } from "./class-user";
import { selectCurrentSection } from "./current";
import {
  selectIsRecordingAgent,
  selectUserIdsWhoAreVisibleInClass,
  selectUserIdsWhoCanParticipateInSection,
} from "./permissions";

const selectClassUsersInVirtualClass = createAppSelector(
  [classUserSelectors.filterForCurrentSection, selectIsClassSessionVirtual],
  (classUsers, isClassSessionVirtual) =>
    isClassSessionVirtual ? classUsers.filter((cu) => cu.is_in_class_session) : [],
);

const selectUserIdsInVirtualClass = createStringArraySelector(
  [selectClassUsersInVirtualClass],
  (classUsers) => classUsers.map((classUser) => classUser.user_id),
);

export const selectVirtualClassAvService = createAppSelector(
  [selectCurrentSection],
  (currentSection) => currentSection?.av_service || null,
);

export const selectVirtualClassLicodeServerUrl = createAppSelector(
  [selectCurrentSection],
  (currentSection) => currentSection?.licode_server_url || null,
);

export const selectVirtualClassOpentokSessionId = createAppSelector(
  [selectActiveClassSession],
  (currentClassSession) => currentClassSession?.opentok_session_id || null,
);
const selectMainRoomScreensharingClassUserId = createAppSelector(
  [selectActiveClassSession],
  (classSession) => classSession?.screensharing_class_user_id || null,
);

const selectCurrentBreakoutScreensharingClassUserId = createAppSelector(
  [selectCurrentBreakoutGroup],
  (breakoutGroup) => breakoutGroup?.screensharing_class_user_id || null,
);

// If the user is in a breakout, return the screensharing user for that group.
// Otherwise, return the main class session screensharing user.
export const selectScreensharingClassUserId = createAppSelector(
  [
    selectMainRoomScreensharingClassUserId,
    selectCurrentBreakoutScreensharingClassUserId,
    selectCurrentBreakoutGroupId,
    selectIsInBreakout,
  ],
  (
    mainRoomScreensharingClassUserId,
    breakoutScreensharingClassUserId,
    breakoutGroupId,
    isInBreakout,
  ) => {
    if (isInBreakout && breakoutGroupId) {
      return breakoutScreensharingClassUserId;
    }
    return mainRoomScreensharingClassUserId;
  },
);

export const selectIsScreensharingOn = createAppSelector(
  [selectScreensharingClassUserId],
  (screensharingClassUserId) => !!screensharingClassUserId,
);

export const selectCanStudentsScreenshare = createAppSelector(
  [selectActiveClassSession],
  (classSession) => classSession?.can_students_screenshare || false,
);

export const selectViewingScreenshare = createAppSelector(
  [
    selectIsRecordingAgent,
    selectIsScreensharingOn,
    (state: RootState) => state.view.viewingScreenshare,
  ],
  (isRecordingAgent, isScreensharingOn, viewingScreenshare) =>
    (isRecordingAgent && isScreensharingOn) || viewingScreenshare,
);

export const selectCountStudentsInVirtualClass = createAppSelector(
  [selectUserIdsWhoCanParticipateInSection, selectUserIdsInVirtualClass],
  (studentUserIds, userIdsInVirtualClass) => {
    const userIdsInVirtualClassSet = new Set(userIdsInVirtualClass);
    return studentUserIds.filter((userId) => userIdsInVirtualClassSet.has(userId)).length;
  },
);

export const selectClassUsersWithIsVisibleInClassCapability = createAppSelector(
  [selectClassUsersInVirtualClass, selectUserIdsWhoAreVisibleInClass],
  (classUsersInVirtualClass, visibleUserIds) => {
    const visibleUserIdSet = new Set(visibleUserIds);
    return classUsersInVirtualClass.filter((classUser) => visibleUserIdSet.has(classUser.user_id));
  },
);

export const selectClassUserIdsWithIsVisibleInClassCapability = createStringArraySelector(
  [selectClassUsersWithIsVisibleInClassCapability],
  (classUsers) => classUsers.map((classUser) => classUser.id),
);

export const selectCurrentUserHasVisibleInClassCapability = createAppSelector(
  [selectCurrentClassUser, selectUserIdsWhoAreVisibleInClass],
  (currentClassUser, visibleUserIds) =>
    !!currentClassUser && visibleUserIds.includes(currentClassUser.user_id),
);

export const selectClassUsersInMainRoom = createAppSelector(
  [selectClassUsersWithIsVisibleInClassCapability],
  (classUsers) => classUsers.filter((classUser) => !classUser.breakout_group_id),
);
